import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { Client, ApiResponse, PaginatedResponse } from '../../types';

interface ClientHistorique {
  demarches: any[];
  reparations: any[];
  solutions: any[];
  factures: any[];
}

interface EspaceClientAuth {
  username: string;
  temp_password: string | null;
  password_changed: boolean;
  is_active: boolean;
  last_login: string | null;
}

interface ClientsState {
  clients: Client[];
  currentClient: Client | null;
  currentClientHistorique: ClientHistorique | null;
  currentClientAuth: EspaceClientAuth | null;
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  } | null;
}

const initialState: ClientsState = {
  clients: [],
  currentClient: null,
  currentClientHistorique: null,
  currentClientAuth: null,
  loading: false,
  error: null,
  pagination: null,
};

// Actions asynchrones
export const fetchClients = createAsyncThunk(
  'clients/fetchAll',
  async (params: { page?: number; limit?: number; search?: string; type_client?: string }) => {
    const response = await api.get<ApiResponse<PaginatedResponse<Client>>>('/clients', { params });
    return response.data.data!;
  }
);

export const fetchClientById = createAsyncThunk(
  'clients/fetchById',
  async (id: number) => {
    const response = await api.get<ApiResponse<{ client: Client; historique: ClientHistorique; espace_client_auth: EspaceClientAuth | null }>>(`/clients/${id}`);
    return response.data.data!;
  }
);

export const createClient = createAsyncThunk(
  'clients/create',
  async (clientData: Partial<Client>) => {
    const response = await api.post<ApiResponse<{ id: number }>>('/clients', clientData);
    return response.data;
  }
);

export const updateClient = createAsyncThunk(
  'clients/update',
  async ({ id, data }: { id: number; data: Partial<Client> }) => {
    const response = await api.put<ApiResponse<null>>(`/clients/${id}`, data);
    return { id, data };
  }
);

export const deleteClient = createAsyncThunk(
  'clients/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete(`/clients/${id}`);
      return id;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la suppression du client';
      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentClient: (state) => {
      state.currentClient = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all clients
    builder.addCase(fetchClients.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchClients.fulfilled, (state, action) => {
      state.loading = false;
      state.clients = action.payload.data;
      state.pagination = action.payload.pagination;
    });
    builder.addCase(fetchClients.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Erreur lors du chargement des clients';
    });

    // Fetch client by ID
    builder.addCase(fetchClientById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchClientById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentClient = action.payload.client;
      state.currentClientHistorique = action.payload.historique;
      state.currentClientAuth = action.payload.espace_client_auth;
    });
    builder.addCase(fetchClientById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Erreur lors du chargement du client';
    });

    // Create client
    builder.addCase(createClient.fulfilled, (state) => {
      state.loading = false;
    });

    // Update client
    builder.addCase(updateClient.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.clients.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.clients[index] = { ...state.clients[index], ...action.payload.data };
      }
    });

    // Delete client
    builder.addCase(deleteClient.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteClient.fulfilled, (state, action) => {
      state.loading = false;
      state.clients = state.clients.filter(c => c.id !== action.payload);
    });
    builder.addCase(deleteClient.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string || 'Erreur lors de la suppression du client';
    });
  },
});

export const { clearError, clearCurrentClient } = clientsSlice.actions;
export default clientsSlice.reducer;
